<script lang="ts" setup>
const emits = defineEmits(['focus', 'blur', 'submit']);

const modelValue = defineModel<string>({ default: '' });

const props = defineProps<{
  name: string;
  error?: string;
  hint?: string;
  placeholder?: string;
  disabled?: boolean;
  maxLen?: number;
  minLen?: number;
  autofocus?: boolean;
}>();

const textarea = ref();

const isFocused = ref(false);
const isHovered = ref(false);

const errorText = ref('');

watch(
  () => props.error,
  () => (errorText.value = props.error ?? ''),
  { immediate: true },
);

const computedClasses = computed(() => [
  { 'textarea-field__focus': isFocused.value },
  { 'textarea-field__hover': isHovered.value },
  { 'textarea-field__error': !!errorText.value },
]);
const isShowBottomText = computed(() => !!errorText.value || !!props.hint);

onMounted(() => {
  if (props.autofocus) focus();
});

function submit() {
  if (modelValue.value.length) {
    emits('submit');
  }
}

function focus() {
  textarea.value && textarea.value.focus();
  isFocused.value = true;
  emits('focus');
}

function blur() {
  textarea.value && textarea.value.blur();
  isFocused.value = false;
  emits('blur');
}

function handleKeyboardEvents(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    blur();
  }
  if (e.key === 'Enter' && e.ctrlKey) {
    modelValue.value += '\n';
    return;
  }
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    submit();
  }
}

const isCounterError = ref(false);

debouncedWatch(
  () => modelValue.value,
  (value) => {
    if (props.minLen) {
      isCounterError.value = value.length !== 0 && value.length < props.minLen;
      errorText.value = isCounterError.value ? `Минимум ${props.minLen} символов` : '';
    }

    if (props.maxLen && !isCounterError.value) {
      isCounterError.value = value.length > props.maxLen;
      errorText.value = isCounterError.value ? 'Превышено количество символов' : '';
    }
  },
  { debounce: 500 },
);
</script>

<template>
  <div>
    <div
      :class="computedClasses"
      class="textarea-field"
      @keydown="handleKeyboardEvents"
    >
      <textarea
        ref="textarea"
        v-model="modelValue"
        :disabled="disabled"
        :name="name"
        :placeholder="placeholder"
        class="textarea-field__textarea"
        rows="4"
        @blur="blur"
        @focus="focus"
        @mouseout="isHovered = false"
        @mouseover="isHovered = true"
      />

      <div
        v-if="maxLen"
        :class="{ 'textarea-field__counter--error': isCounterError }"
        class="textarea-field__counter"
      >
        {{ modelValue.length }}/{{ maxLen }}
      </div>
    </div>

    <FoldableBlock v-model="isShowBottomText">
      <div class="text-xs py-1.5">
        <span
          v-if="errorText"
          class="text-red"
        >
          {{ errorText }}
        </span>

        <span v-else-if="hint">{{ hint }}</span>
      </div>
    </FoldableBlock>
  </div>
</template>

<style lang="scss" scoped>
.textarea-field {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid $black-20;
  border-radius: 8px;
  background-color: $black-5;
  transition: all 0.2s;

  &__hover {
    border-color: $black-40;
  }

  &__focus {
    border-color: $black-80;
  }

  &__textarea {
    width: 100%;
    padding: 8px;
    border-radius: 8px;
    background-color: $black-5;
    color: $black;

    &::placeholder {
      color: $black-40;
    }

    &:hover,
    &:focus {
      outline: none;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      margin: 0;
      appearance: none;
    }

    &:disabled {
      background-color: $black-5;
    }
  }

  &__error {
    border-color: $red;
  }

  &__counter {
    position: absolute;
    right: 6px;
    bottom: 5px;
    padding: 2px;
    border-radius: 6px;
    background-color: $black-5;
    color: $black-40;
    font-size: $text-xs;
    line-height: 1;

    &--error {
      color: $red;
    }
  }
}
</style>
